import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { AppointmentLogEntryDTO } from '../types/agenda.types';
import { getAppointmentLog } from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import useNotify from '../../../hooks/useNotify';
import './AppointmentLogModal.scss';

const dateTimeFormatter = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

interface AppointmentLogModalProps {
  visible: boolean;
  appointmentId: number | null;
  patientName?: string;
  onHide: () => void;
}

export default function AppointmentLogModal({
  visible,
  appointmentId,
  patientName,
  onHide,
}: AppointmentLogModalProps) {
  const notify = useNotify();
  const [entries, setEntries] = useState<AppointmentLogEntryDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !appointmentId) return;
    setLoading(true);
    getAppointmentLog(appointmentId)
      .then(setEntries)
      .catch((err) => {
        // Error general: card flotante y se cierra (se reintenta con el mismo ícono del turno)
        notify.error(parseApiError(err, 'No se pudo cargar el historial del turno. Intentá de nuevo.'));
        onHide();
      })
      .finally(() => setLoading(false));
  }, [visible, appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const header = patientName ? `Historial de cambios · ${patientName}` : 'Historial de cambios';

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      modal
      closable
      draggable={false}
      className="nv-scope nv-dialog"
      style={{ width: '600px', maxWidth: '95vw' }}
      footer={<Button type="button" className="buttonMain2" outlined label="Cerrar" onClick={onHide} />}
    >
      {loading && (
        <div className="nv-loading" role="status">
          <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…
        </div>
      )}

      {!loading && entries.length === 0 && (
        <p className="appt-log__empty">Todavía no hay cambios registrados.</p>
      )}

      {!loading && entries.length > 0 && (
        <ol className="appt-log__timeline">
          {entries.map((entry, idx) => (
            <li key={idx} className="appt-log__entry">
              <span className="appt-log__entry-dot" aria-hidden />
              <div className="appt-log__entry-content">
                <div className="appt-log__entry-header">
                  <span className="nv-strong">{entry.actionDescription}</span>
                  <span className="nv-help appt-log__entry-date">
                    {dateTimeFormatter.format(new Date(entry.changeDate))}
                  </span>
                </div>
                <div className="nv-row-8">
                  <span>{entry.stateName}</span>
                  <span className="nv-muted">{entry.fullName}</span>
                </div>
                {entry.notes && Object.keys(entry.notes).length > 0 && (
                  <div className="appt-log__entry-notes">
                    {Object.entries(entry.notes).map(([key, value]) => (
                      <div key={key}>
                        <span className="nv-strong appt-log__entry-note-key">{key}:</span> {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}
